import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";
import { FiPlus, FiTrash2 } from "react-icons/fi";

function formatTime12(value) {
  if (!value) return "";
  // HTML time input is HH:MM (24h) → display like "3:15 PM"
  const [hStr, mStr] = value.split(":");
  let h = Number(hStr);
  const m = mStr || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

function toInputTime(display) {
  if (!display) return "09:00";
  if (/^\d{1,2}:\d{2}$/.test(display)) return display.padStart(5, "0");
  const match = String(display).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return "09:00";
  let h = Number(match[1]);
  const m = match[2];
  const ap = match[3].toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m}`;
}

export default function AddMedicineReminder() {
  const { ApiBaseUrl, setIsLoginShow } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { reminderId } = useParams();
  const edit = location.state || (reminderId ? { id: reminderId } : {});
  const Userid = secureLocalStorage.getItem("Userid");

  const [medicineName, setMedicineName] = useState(edit.medicineName || "");
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(String(edit.status ?? "1") === "1");
  const [doses, setDoses] = useState(() => {
    if (edit.timedata?.length) {
      return edit.timedata.map((t, i) => ({
        title: t.title || `Dose ${i + 1}`,
        time: toInputTime(t.time),
      }));
    }
    return [{ title: "Dose 1", time: "09:00" }];
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!Userid) setIsLoginShow(true);
  }, [Userid, setIsLoginShow]);

  useEffect(() => {
    const q = medicineName.trim();
    if (q.length < 1) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      axios
        .post(`${ApiBaseUrl}/customer/search_meidicine`, { text: q })
        .then((r) => setSuggestions(r.data.medicine_list || r.data.list || []))
        .catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [medicineName, ApiBaseUrl]);

  const save = () => {
    if (!Userid) {
      setIsLoginShow(true);
      return;
    }
    if (!medicineName.trim()) {
      showToast({ title: "Enter medicine name", id: "error" });
      return;
    }
    const time = doses.map((d, i) => ({
      id: i + 1,
      title: d.title || `Dose ${i + 1}`,
      time: formatTime12(d.time),
    }));
    setSaving(true);
    const reminderId = edit.id || reminderId;
    const body = {
      id: Userid,
      medicine_name: medicineName.trim(),
      time,
      status: active ? "1" : "0",
    };
    const url = reminderId
      ? `${ApiBaseUrl}/customer/edit_medicince_reminder`
      : `${ApiBaseUrl}/customer/add_medicince_reminder`;
    const payload = reminderId ? { ...body, reminder_id: reminderId } : body;

    axios
      .post(url, payload)
      .then((r) => {
        if (r.data.Result) {
          showToast({ title: r.data.message || "Saved", id: "success" });
          navigate("/dashbord/medicine-reminder");
        } else {
          showToast({ title: r.data.message || "Save failed", id: "error" });
        }
      })
      .catch(() => showToast({ title: "Network error", id: "error" }))
      .finally(() => setSaving(false));
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-rm-ink mb-2">
        {reminderId ? "Edit reminder" : "Add medicine"}
      </h1>
      <p className="text-gray-600 text-sm mb-6">Set medicine name and daily dose times.</p>

      <label className="block text-sm font-medium mb-1">Medicine name</label>
      <input
        className="w-full border rounded-lg px-3 py-2 mb-1"
        value={medicineName}
        onChange={(e) => setMedicineName(e.target.value)}
        placeholder="e.g. Paracetamol"
      />
      {suggestions.length > 0 && (
        <div className="border rounded-lg mb-4 max-h-40 overflow-auto bg-white">
          {suggestions.slice(0, 8).map((s, i) => {
            const name = s.name || s.medicine_name || s.title || String(s);
            return (
              <button
                key={i}
                type="button"
                className="block w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                onClick={() => {
                  setMedicineName(name);
                  setSuggestions([]);
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-4 mb-3">
        <h2 className="font-semibold">Dose times</h2>
        <button
          type="button"
          className="text-[#0d9488] text-sm flex items-center gap-1"
          onClick={() =>
            setDoses((d) => [...d, { title: `Dose ${d.length + 1}`, time: "14:00" }])
          }
        >
          <FiPlus /> Add dose
        </button>
      </div>

      <div className="space-y-3 mb-4">
        {doses.map((d, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              className="border rounded-lg px-3 py-2 flex-1"
              value={d.title}
              onChange={(e) => {
                const next = [...doses];
                next[i] = { ...next[i], title: e.target.value };
                setDoses(next);
              }}
            />
            <input
              type="time"
              className="border rounded-lg px-3 py-2"
              value={d.time}
              onChange={(e) => {
                const next = [...doses];
                next[i] = { ...next[i], time: e.target.value };
                setDoses(next);
              }}
            />
            {doses.length > 1 && (
              <button
                type="button"
                className="p-2 text-red-500"
                onClick={() => setDoses((arr) => arr.filter((_, idx) => idx !== i))}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 mb-6 cursor-pointer">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span className="text-sm">Reminder active</span>
      </label>

      <div className="flex gap-3">
        <button
          type="button"
          className="px-4 py-2 rounded-lg border"
          onClick={() => navigate("/dashbord/medicine-reminder")}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-[#0d9488] text-white disabled:opacity-60"
          onClick={save}
        >
          {saving ? "Saving…" : "Save reminder"}
        </button>
      </div>
    </div>
  );
}
